'use client';

import { useMotionValueEvent, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { playSfx } from '@/hooks/use-sound';
import { trackEvent } from '@/lib/events';

import {
  SEGMENTS_AFTER,
  SEGMENTS_BEFORE,
  TOTAL_AFTER,
  TOTAL_BEFORE,
} from '../data';
import { RaceControls } from './race-controls';
import { RaceLane } from './race-lane';
import { RaceLegend } from './race-legend';
import { RaceScrubber } from './race-scrubber';
import { useSimClock } from './use-sim-clock';

type RacePhase = 'idle' | 'playing' | 'paused' | 'finished';

/**
 * The signature interactive: the same five-step run, before and after, racing
 * on a shared time axis. Auto-plays once when scrolled into view; a scrub
 * slider drags through simulated time. With reduced motion the race renders
 * pre-finished so the numbers tell the story without animation.
 */
export function Race() {
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<RacePhase>('idle');
  const phaseRef = useRef<RacePhase>('idle');
  phaseRef.current = phase;

  const { simTime, speedRef, start, stop, seek } = useSimClock(
    TOTAL_BEFORE,
    () => {
      setPhase('finished');
      if (phaseRef.current === 'playing') playSfx('close');
    }
  );
  // Single speed: the animation is narrative, not an instrument to operate.
  useEffect(() => {
    speedRef.current = 120;
  }, [speedRef]);

  // Reduced motion: render the end state (both lanes finished, numbers up).
  useEffect(() => {
    if (reduceMotion) seek(TOTAL_BEFORE);
  }, [reduceMotion, seek]);

  // The after lane finishing mid-playback gets its own tick. Gated on a ref
  // set synchronously by the scrubber, not on `phase`: a state update queued
  // in the same handler that moves the clock has not landed yet, so a scrub
  // across the 300s mark would otherwise still read as "playing".
  const scrubbingRef = useRef(false);
  const afterDoneRef = useRef(false);
  useMotionValueEvent(simTime, 'change', (t) => {
    if (t < TOTAL_AFTER) {
      afterDoneRef.current = false;
    } else if (!afterDoneRef.current) {
      afterDoneRef.current = true;
      if (phaseRef.current === 'playing' && !scrubbingRef.current) {
        playSfx('toggle');
      }
    }
  });

  const play = (trigger: 'auto' | 'button') => {
    if (phaseRef.current === 'playing') return;
    if (simTime.get() >= TOTAL_BEFORE) seek(0);
    playSfx('open');
    setPhase('playing');
    start();
    trackEvent({
      name: 'case_study_race_play',
      properties: { speed: speedRef.current, trigger },
    });
  };

  // One analytics event per scrub session, where a session is "not already
  // scrubbing, and not within a few seconds of the last one" — keyboard
  // auto-repeat would otherwise emit one event per key step.
  const lastScrubTrackRef = useRef(0);
  const scrubStart = (seconds: number) => {
    stop();
    scrubbingRef.current = true;
    setPhase('paused');

    const now = performance.now();
    if (now - lastScrubTrackRef.current > 3000) {
      lastScrubTrackRef.current = now;
      trackEvent({
        name: 'case_study_race_scrub',
        properties: { at: Math.round(seconds) },
      });
    }
  };
  const scrubEnd = () => {
    scrubbingRef.current = false;
    if (simTime.get() >= TOTAL_BEFORE) setPhase('finished');
  };

  // Auto-play once, when ~half the race is visible. Never under reduced motion.
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayedRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `play` is behaviourally stable; re-running the effect per render would re-arm autoplay
  useEffect(() => {
    const el = containerRef.current;
    if (!el || reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !autoPlayedRef.current) {
          autoPlayedRef.current = true;
          observer.disconnect();
          play('auto');
        }
      },
      { threshold: 0.45 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const playLabel =
    phase === 'playing'
      ? 'running…'
      : phase === 'finished'
        ? '▶ Run again'
        : '▶ Run both';

  return (
    <div ref={containerRef} className="border border-edge bg-background">
      <p className="sr-only" aria-live="polite">
        {phase === 'finished'
          ? `Race finished: the before run took ${TOTAL_BEFORE} seconds, the after run took ${TOTAL_AFTER} seconds. That is ${TOTAL_BEFORE - TOTAL_AFTER} seconds faster with identical AI generation time.`
          : `Two simulated workflow runs on a shared time axis: before (${TOTAL_BEFORE}s) and after (${TOTAL_AFTER}s). The grey segments are AI generation, identical in both. The hatched segments are pipeline overhead.`}
      </p>

      <RaceControls
        playLabel={playLabel}
        simTime={simTime}
        cap={TOTAL_BEFORE}
        onPlay={() => play('button')}
      />

      <RaceScrubber
        simTime={simTime}
        onScrubStart={scrubStart}
        onScrubEnd={scrubEnd}
      />

      <div aria-hidden className="flex flex-col gap-5 p-3 pt-4">
        <RaceLane
          tag="Before"
          note="one shared queue · public-internet hops · 5-minute repair sweep"
          tone="before"
          segments={SEGMENTS_BEFORE}
          totalSeconds={TOTAL_BEFORE}
          simTime={simTime}
        />
        <RaceLane
          tag="After"
          note="split lanes · internal bindings · 1-minute repair pass"
          tone="after"
          segments={SEGMENTS_AFTER}
          totalSeconds={TOTAL_AFTER}
          simTime={simTime}
        />
      </div>

      <RaceLegend />

      <div className="border-t border-edge bg-muted/30 p-3 text-sm text-muted-foreground">
        <b className="font-medium text-foreground">The point:</b> the grey is
        the same length in both tracks. Every second we won came out of the
        hatched sections: queueing, hops, bookkeeping.
      </div>
    </div>
  );
}
